import React, { Fragment } from 'react';
import Modal from '@tencent/dui/lib/components/Modal';
import Button from '@tencent/dui/lib/components/Button';
import i18n from '@i18next/i18n';
import { IReactionDisposer, reaction } from 'mobx';
import { IScaleConfirmDialogProps } from '@src/slide/application/service/slide-size-service/index.interface';

import {
    convertCmToPx,
    cmToEmu,
} from '@src/slide/infrastructure/module/independent/unit';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';

enum E_SCALE_TYPE {
    enlarge,
    shrink,
    enlargeOrShrink,
}

interface IScaleConfirmDialogState {
    visible: boolean;
}

export class ScaleConfirmDialog extends React.PureComponent<IScaleConfirmDialogProps, IScaleConfirmDialogState> {
    private reaction: IReactionDisposer;
    public constructor(props: IScaleConfirmDialogProps) {
        super(props);
        this.state = {
            visible: this.props.slideSizeService.confirmVisible,
        };
        this.initReaction();
    }

    public componentWillUnmount() {
        this.reaction?.();
    }

    public render() {
        return <Modal
            zIndex={9999}
            visible={this.state.visible}
            title={i18n.t('确认')}
            onCancel={() => {
                this.props.slideSizeService.confirmVisible = false;
                this.props.onCancel?.();
            }}
            footer={this.renderConfirmFooter()}
        >
            { this.getConfirmText(this.getScaleType()) }
        </Modal>;
    }

    private renderConfirmFooter = () => {
        const type = this.getScaleType();
        return <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 40,
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                { type === E_SCALE_TYPE.enlargeOrShrink
                    ? <Fragment>
                        <Button
                            style={{
                                marginRight: 16,
                            }}
                            type="primary"
                            onClick={() => {
                                this.handleEnlargeOrReduceCommit(false);
                                this.props.onSuccess?.();
                            }}
                        >
                            { i18n.t('缩小') }
                        </Button>
                        <Button
                            onClick={() => {
                                this.handleEnlargeOrReduceCommit(true);
                                this.props.onSuccess?.();
                            }}
                        >
                            { i18n.t('放大') }
                        </Button></Fragment> : <Fragment>
                        <Button
                            style={{
                                marginRight: 16,
                            }}
                            onClick={() => {
                                this.handleNoScale();
                                this.props.onSuccess?.();
                            }}>
                            { i18n.t('不缩放') }
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => {
                                this.handleScaleCommit();
                                this.props.onSuccess?.();
                            }}
                        >
                            { i18n.t('缩放') }
                        </Button>
                    </Fragment> }
            </div>
        </div>;
    };

    private getScale = () => {
        const { slideWidth, slideHeight } = this.props.uiDataService.presentation.props;
        const newSlideWidth = Math.round(convertCmToPx(this.props.slideWidth));
        const newSlideHeight = Math.round(convertCmToPx(this.props.slideHeight));
        const scaleX = newSlideWidth / Math.round(slideWidth);
        const scaleY = newSlideHeight / Math.round(slideHeight);
        if (Math.abs(scaleX - 1) > Math.abs(scaleY - 1)) {
            return scaleX;
        }

        return scaleY;
    };

    private getEnlargeOrReduceScale = (isEnlarge: boolean) => {
        const { slideWidth, slideHeight } = this.props.uiDataService.presentation.props;
        const newSlideWidth = Math.round(convertCmToPx(this.props.slideWidth));
        const newSlideHeight = Math.round(convertCmToPx(this.props.slideHeight));
        const scaleX = newSlideWidth / Math.round(slideWidth);
        const scaleY = newSlideHeight / Math.round(slideHeight);
        if (isEnlarge) {
            return Math.max(scaleX, scaleY);
        }

        return Math.min(scaleX, scaleY);
    };

    /**
     * 自定义页面比例，不缩放元素
     */
    private handleNoScale = () => {
        const { presentation } = this.props.uiDataService;
        const slideWidth = cmToEmu(this.props.slideWidth);
        const slideHeight = cmToEmu(this.props.slideHeight);
        presentation.updateCustomSlideSizeWithNoScale(slideWidth, slideHeight, this.props.slideSizeType);
        this.props.slideSizeService.hideDialog();
        this.report(slideWidth, slideHeight);
    };

    /**
     * 放大
     */
    private handleEnlargeOrReduceCommit = (isEnlarge: boolean) => {
        const { presentation } = this.props.uiDataService;
        const scale = this.getEnlargeOrReduceScale(isEnlarge);
        const emuWidth = cmToEmu(this.props.slideWidth);
        const emuHeight = cmToEmu(this.props.slideHeight);
        presentation.updateCustomSlideSizeWithEnlargeOrReduce(
            emuWidth,
            emuHeight,
            isEnlarge,
            this.props.slideSizeType,
        );
        this.props.slideSizeService.hideDialog();
        this.report(emuWidth, emuHeight);
    };


    private handleScaleCommit = () => {
        const { presentation } = this.props.uiDataService;
        const scale = this.getScale();
        const slideWidth = cmToEmu(this.props.slideWidth);
        const slideHeight = cmToEmu(this.props.slideHeight);
        presentation.updateCustomSlideSize(slideWidth, slideHeight, scale, this.props.slideSizeType);
        this.props.slideSizeService.hideDialog();
        this.report(slideWidth, slideHeight);
    };

    /**
     * 获取操作类型
     */
    private getScaleType = () => {
        const { slideWidth, slideHeight } = this.props.uiDataService.presentation.props;
        const newSlideWidth = Math.round(convertCmToPx(this.props.slideWidth));
        const newSlideHeight = Math.round(convertCmToPx(this.props.slideHeight));

        if (newSlideWidth >= Math.round(slideWidth) && newSlideHeight >= Math.round(slideHeight)) {
            return E_SCALE_TYPE.enlarge;
        }

        if (newSlideWidth <= Math.round(slideWidth) && newSlideHeight <= Math.round(slideHeight)) {
            return E_SCALE_TYPE.shrink;
        }

        return E_SCALE_TYPE.enlargeOrShrink;
    };

    private getConfirmText = (type: E_SCALE_TYPE) => {
        switch (type) {
            case E_SCALE_TYPE.enlarge:
                return i18n.t('将转换为更大的幻灯片。是否放大内容');
            case E_SCALE_TYPE.shrink:
                return i18n.t('将转换为更小的幻灯片。是否缩小内容');
            default:
                return i18n.t('是否放大或缩小内容');
        }
    };

    private initReaction = () => {
        this.reaction = reaction(() => this.props.slideSizeService.confirmVisible, (visible) => {
            this.setState({
                visible,
            });
        });
    };

    private report = (slideWidth: number, slideHeight: number) => {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.BEAUTIFY_TAB,
            action: TDWActionType.BEAUTIFY_SLIDE_CUSTOM_SIZE,
            ver5: slideWidth / slideHeight,
        });
    };
}
